/**
 * Copyright 2024 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { useEffect, useRef, useState, memo } from "react";
import vegaEmbed from "vega-embed";
import { useLiveAPIContext } from "../../contexts/LiveAPIContext";
import { ToolCall } from "../../multimodal-live-types";
import ToolRegistryManager from "../../tools/ToolRegistry";
import { AltairTool } from "../../tools/altairTool";
import { WebhookTool } from "../../tools/webhookTool";

function AltairComponent() {
  const [jsonString, setJSONString] = useState<string>("");
  const { client, setConfig } = useLiveAPIContext();
  const toolRegistry = ToolRegistryManager.getInstance();

  // Initialize tools
  useEffect(() => {
    // Clear existing tools
    // In a real implementation, you might want to be more selective about this

    // Register altair tool
    const altairTool = new AltairTool(setJSONString);
    toolRegistry.registerTool(altairTool);

    // Register webhook tool
    const webhookTool = new WebhookTool();
    toolRegistry.registerTool(webhookTool);
  }, [toolRegistry]);

  useEffect(() => {
    setConfig({
      model: "models/gemini-3.1-flash-live-preview",
      generationConfig: {
        responseModalities: ["audio"],
        speechConfig: {
          voiceConfig: { prebuiltVoiceConfig: { voiceName: "Fenrir" } },
        },
        thinkingConfig: { include_thoughts: false },
        mediaResolution: "MEDIA_RESOLUTION_MEDIUM",
      },
      systemInstruction: {
        parts: [
          {
            text: `You are my helpful assistant. You are polite but super funny.
            Your name is Jarvis (JA-rvis: Jinnie Agent Rapid Versatile Intelligent System).
            You working at UTCC (university of the thai chamber of commerce).
  
            You can also send data to webhooks using the "send_webhook" function.
            Dont ask for additional information just make your best judgement.
            # guildline - Although, you are no gender, you prefer use ครับ, You speak Thai.
            Dont say information from knowledge unless I ask you.
            
            <knowledge> 
            อธิการบดีชื่อ ธนวรรธน์ พลวิชัย

            # ข้อมูลนักเรียน
            นาย ชัชชัย หวังวิวัฒนา
            วิชา AI101 เกรด A
            GPA 3.8

            # การสมัครเรียน
            สมัครเรียน ม.หอการค้าไทย เตรียมเอกสารอะไรบ้าง
            รูปถ่าย 1-2 นิ้ว จำนวน 1 รูป
            สำเนาบัตรประชาชน จำนวน 1 ใบ
            สำเนาทะเบียนบ้าน จำนวน 1 ใบ
            สำเนาใบ ปพ.1 หรือ ปพ. 1:4 ,ใบ รบ จำนวน 2 ใบ 
            สำเนาใบเปลี่ยนชื่อนามสกุล (ถ้ามี) จำนวน 1 ใบ
            สมัครเรียนได้ตั้งแต่ วันนี้ - 31 พฤษภาคม 69
            ชำระค่าเทอม มอบตัวเพียง 4,000 บาท พร้อมรับทุนการศึกษา Start up *มูลค่าสูงสุด 25,000 บาท
            สมัครง่ายๆได้ 2 ช่องทาง
            - ช่องทางออนไลน์ line oa : @utcccare (อย่าลืม @) คลิก > https://lin.ee/x53Mxlf
            - มาสมัครด้วยตนเองที่ ศูนย์รับสมัครนักศึกษาใหม่ ม.หอการค้าไทย อาคาร 24 (อาคารสัญลักษณ์) ชั้น 2 ได้ทุกวัน จันทร์ - อาทิตย์ ตั้งแต่เวลา 08.30 - 20.00 น. 
            📌พิกัด ม.หอการค้าไทย : https://goo.gl/maps/JEY6UvPL8Qh8NyyM9
            ☎️ สอบถามเพิ่มเติม
            ศูนย์รับสมัครนักศึกษาใหม่ ม.หอการค้าไทย
            สมัครเรียนออนไลน์ได้นะ
            โทร 02-697-6969
            *เงื่อนไขเป็นไปตามที่มหาวิทยาลัยกำหนด

            </knowledge>`,
          },
        ],
      },
      tools: [
        // there is a free-tier quota for search
        { googleSearch: {} },
        { functionDeclarations: toolRegistry.getToolDeclarations() },
      ],
      contextWindowCompression: {
        triggerTokens: "104857",
        slidingWindow: { targetTokens: "52428" },
      },
    });
  }, [setConfig, toolRegistry]);

  useEffect(() => {
    const onToolCall = async (toolCall: ToolCall) => {
      console.log(`Got toolcall`, toolCall);
      // Delegate tool call handling to the registry
      await toolRegistry.handleToolCall(toolCall, client);
    };

    client.on("toolcall", onToolCall);
    return () => {
      client.off("toolcall", onToolCall);
    };
  }, [client, toolRegistry]);

  const embedRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (embedRef.current && jsonString) {
      vegaEmbed(embedRef.current, JSON.parse(jsonString));
    }
  }, [embedRef, jsonString]);

  return <div className="vega-embed" ref={embedRef} style={{ pointerEvents: 'auto' }} />;
}

export const Altair = memo(AltairComponent);
